import {
    DIECASTS_MAIN_CATEGORY,
    diecastSubCategoriesIdPairs,
    raceUsedSubCategoriesIdPairs,
} from '../constants/dropdownIdPairs';

/** Default main category for race-used vault browse (non-diecast). */
export const DEFAULT_RACE_USED_MAIN_CATEGORY = 'Race Used / Team Issued';

/** Browse every main category (no MainCategory filter, no ExcludeMainCategory). */
export const ALL_ITEMS_MAIN_CATEGORY = '';

/** Wishlist-only category: marketplace listings saved to the wish list. */
export const MARKETPLACE_WISHLIST_CATEGORY = '__marketplace__';

/** Collectible browse categories shown in the vault sidebar (matches Marketplace). */
export const VAULT_RACE_USED_BROWSE_CATEGORIES = [
    { name: ALL_ITEMS_MAIN_CATEGORY, label: 'All items' },
    { name: DEFAULT_RACE_USED_MAIN_CATEGORY, label: 'Race used' },
    { name: DIECASTS_MAIN_CATEGORY, label: 'Diecasts' },
    { name: 'Trading Cards', label: 'Trading cards' },
    { name: 'Hero Cards / Post Cards', label: 'Hero cards' },
    { name: 'Programs / Tickets', label: 'Programs' },
    { name: 'Misc.', label: 'Misc.' },
];

/** Wishlist category list — same as vault, with For Sale above All items. */
export const WISHLIST_BROWSE_CATEGORIES = [
    { name: MARKETPLACE_WISHLIST_CATEGORY, label: 'For Sale' },
    ...VAULT_RACE_USED_BROWSE_CATEGORIES,
];

/** Parse mainCategory from URL: missing param → default race used; empty param → all items. */
export const parseVaultMainCategoryFromUrl = (params) => {
    if (!params.has('mainCategory')) {
        return DEFAULT_RACE_USED_MAIN_CATEGORY;
    }
    const value = params.get('mainCategory');
    return value === '' ? ALL_ITEMS_MAIN_CATEGORY : value;
};

/** Restore mainCategory from session storage; only fill default when value was never saved. */
export const restoreVaultMainCategory = (mainCategory) => (
    mainCategory === undefined || mainCategory === null
        ? DEFAULT_RACE_USED_MAIN_CATEGORY
        : mainCategory
);

/** API value: empty / all-items → null; otherwise pass through. */
export const resolveMainCategoryForApi = (mainCategory) => (
    mainCategory ? mainCategory : null
);

export const resolveVaultCategoryLabel = (mainCategory, isDiecastsMode = false) => {
    if (mainCategory === MARKETPLACE_WISHLIST_CATEGORY) return 'For Sale';
    if (isDiecastsMode || mainCategory === DIECASTS_MAIN_CATEGORY) return 'Diecasts';
    if (mainCategory === ALL_ITEMS_MAIN_CATEGORY) return 'All items';
    if (!mainCategory || mainCategory === DEFAULT_RACE_USED_MAIN_CATEGORY) return 'Race used';
    return VAULT_RACE_USED_BROWSE_CATEGORIES.find((c) => c.name === mainCategory)?.label
        || mainCategory;
};

/** Small green eyebrow above the results title (always Collectibles on vault browse). */
export const resolveVaultSectionEyebrow = (_isDiecastsMode = false) => 'Collectibles';

export const resolveVaultSectionTitle = (formData, isDiecastsMode, subCategory) => {
    if (isDiecastsMode) return 'Diecasts';
    if (subCategory) return subCategory;
    return resolveVaultCategoryLabel(formData?.mainCategory, false);
};

/** Empty-feed copy for vault browse grids. */
export const resolveVaultEmptyMessage = (formData, isDiecastsMode = false) => {
    if (isDiecastsMode || formData?.mainCategory === DIECASTS_MAIN_CATEGORY) {
        return 'No Diecasts Found';
    }
    const label = resolveVaultCategoryLabel(formData?.mainCategory, false);
    if (label === 'All items') return 'No Items Found';
    if (label === 'Race used') return 'No Race-used Items Found';
    return `No ${label} Found`;
};

export const isDefaultRaceUsedCategory = (mainCategory) => (
    mainCategory === DEFAULT_RACE_USED_MAIN_CATEGORY
);

export const isDiecastsBrowseCategory = (mainCategory) => (
    mainCategory === DIECASTS_MAIN_CATEGORY
);

/** When switching main category crosses collectibles ↔ diecasts vault mode. */
export const resolveCategoryModeSwitch = (nextForm, isDiecastsMode) => {
    const nextMain = nextForm?.mainCategory;
    const clearedSubs = {
        subCategory: '',
        subSubCategory: '',
        subSubSubCategory: '',
    };

    if (!isDiecastsMode && nextMain === DIECASTS_MAIN_CATEGORY) {
        return {
            isDiecastsMode: true,
            form: { ...nextForm, mainCategory: DIECASTS_MAIN_CATEGORY, ...clearedSubs },
        };
    }

    if (isDiecastsMode && nextMain !== DIECASTS_MAIN_CATEGORY) {
        return {
            isDiecastsMode: false,
            form: {
                ...nextForm,
                mainCategory: nextMain ?? DEFAULT_RACE_USED_MAIN_CATEGORY,
                ...clearedSubs,
            },
        };
    }

    return { isDiecastsMode, form: nextForm };
};

export const getVaultActiveSubcategories = (formData, isDiecastsMode) => {
    if (isDiecastsMode) {
        return diecastSubCategoriesIdPairs;
    }
    if (formData?.mainCategory === MARKETPLACE_WISHLIST_CATEGORY) {
        return [];
    }
    if (formData?.mainCategory === DEFAULT_RACE_USED_MAIN_CATEGORY) {
        return raceUsedSubCategoriesIdPairs;
    }
    return [];
};

export const toggleVaultMainCategory = (formData, name, _isDiecastsMode = false) => {
    // Always apply the clicked category. Mode switch (collectibles ↔ diecasts routes)
    // is handled by resolveCategoryModeSwitch / buildCollectibleCategorySearch.
    return {
        ...formData,
        mainCategory: name,
        subCategory: '',
        subSubCategory: '',
        subSubSubCategory: '',
    };
};

export const toggleVaultSubCategory = (formData, name, isDiecastsMode) => {
    const next = formData.subCategory === name ? '' : name;
    return {
        ...formData,
        mainCategory: isDiecastsMode ? DIECASTS_MAIN_CATEGORY : formData.mainCategory,
        subCategory: next,
        subSubCategory: '',
        subSubSubCategory: '',
    };
};

export const buildVaultCollectibleFilterChips = (formData, isDiecastsMode, extras = {}) => {
    const chips = [];
    const data = formData || {};

    // Always show the active category chip (Race used, Diecasts, Trading cards, All items, etc.).
    const categoryLabel = isDiecastsMode
        ? 'Diecasts'
        : resolveVaultCategoryLabel(data.mainCategory, false);
    chips.push({ key: 'mainCategory', label: categoryLabel });

    if (data.subCategory) {
        chips.push({ key: 'subCategory', label: data.subCategory });
    }
    if (data.subSubCategory) {
        chips.push({ key: 'subSubCategory', label: data.subSubCategory });
    }
    if (data.subSubSubCategory) {
        chips.push({ key: 'subSubSubCategory', label: data.subSubSubCategory });
    }
    if (data.driverName) {
        chips.push({ key: 'driverName', label: data.driverName });
    }
    if (data.series) {
        chips.push({ key: 'series', label: data.series });
    }
    if (extras.hasLikes) {
        chips.push({ key: 'hasLikes', label: 'Liked items' });
    }
    return chips;
};

export const removeVaultCollectibleFilterChip = (formData, key, isDiecastsMode) => {
    const data = { ...formData };

    if (key === 'mainCategory') {
        // Clearing category returns to All items (and exits Diecasts mode via category switch).
        return {
            data: {
                ...data,
                mainCategory: ALL_ITEMS_MAIN_CATEGORY,
                subCategory: '',
                subSubCategory: '',
                subSubSubCategory: '',
            },
        };
    }
    if (key === 'subCategory') {
        return { data: toggleVaultSubCategory(data, data.subCategory, isDiecastsMode) };
    }
    if (key === 'hasLikes') {
        return { data, clearHasLikes: true };
    }
    if (key === 'driverName') {
        return { data: { ...data, driverName: '' } };
    }
    if (key === 'series') {
        return { data: { ...data, series: '' } };
    }
    if (key === 'subSubCategory') {
        return { data: { ...data, subSubCategory: '', subSubSubCategory: '' } };
    }
    if (key === 'subSubSubCategory') {
        return { data: { ...data, subSubSubCategory: '' } };
    }
    return { data };
};

export const countVaultDrawerFilters = (formData, isDiecastsMode) => {
    const data = formData || {};
    const mainCat = isDiecastsMode ? DIECASTS_MAIN_CATEGORY : data.mainCategory;
    let count = 0;

    if (isDiecastsMode) {
        if (data.subSubCategory) count += 1;
        if (data.subSubSubCategory) count += 1;
        return count;
    }

    if (
        (mainCat === 'Trading Cards'
            || mainCat === 'Programs / Tickets'
            || mainCat === 'Hero Cards / Post Cards')
        && data.subCategory
    ) {
        count += 1;
    }
    if (mainCat === 'Trading Cards' && data.subSubCategory) {
        count += 1;
    }
    if (mainCat === 'Race Used / Team Issued' && data.subSubCategory) {
        count += 1;
    }
    if (mainCat === 'Trading Cards' && data.subSubSubCategory) {
        count += 1;
    }

    return count;
};
